import { useEffect, useRef, useState } from 'react';
import { Howl } from 'howler';
import { Loader2, Pause, Play } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { useFetchRecordingUrl } from '../../hooks/use-recordings';
import { formatCallDuration } from '../../utils/call-status-meta';
import type { IVoiceRecordingSummary } from '../../types';

type EngineStatus = 'idle' | 'loading' | 'ready' | 'error';

interface HistoryRecordingPlayerProps {
    /** Render only for `STORED` rows — the caller guards. */
    readonly recording: IVoiceRecordingSummary;
}

function clamp01(value: number): number {
    return Math.min(Math.max(value, 0), 1);
}

/**
 * Compact single-row recording player for the widget's per-number timeline —
 * play/pause, a slim progress track, and `elapsed / total`. Same engine
 * contract as the calls page's `RecordingCard` (presign fetched inside the
 * first Play gesture so autoplay stays user-initiated; one html5 Howl per
 * url, unloaded on unmount) minus the transport/download/delete chrome that
 * doesn't fit the widget's width.
 */
export function HistoryRecordingPlayer({ recording }: HistoryRecordingPlayerProps) {
    const soundRef = useRef<Howl | null>(null);
    const [url, setUrl] = useState<string | null>(null);
    const [engine, setEngine] = useState<EngineStatus>('idle');
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [position, setPosition] = useState(0);
    const fetchUrl = useFetchRecordingUrl();

    // One Howl per fetched url; created only after the user's first Play,
    // unloaded on unmount. The 'loading' transition happens in the Play click
    // (with the url), so this effect never sets state synchronously.
    useEffect(() => {
        if (!url) return undefined;
        const sound = new Howl({ src: [url], html5: true });
        soundRef.current = sound;

        sound.on('load', () => {
            setDuration(sound.duration());
            setEngine('ready');
        });
        sound.on('loaderror', () => setEngine('error'));
        // Autoplay can be refused (gesture consumed) — not a load failure;
        // keep the player usable so the Play button works on click.
        sound.on('playerror', () => setIsPlaying(false));
        sound.on('play', () => setIsPlaying(true));
        sound.on('pause', () => setIsPlaying(false));
        sound.on('stop', () => setIsPlaying(false));
        sound.on('end', () => {
            setIsPlaying(false);
            setPosition(0);
        });
        sound.play();

        return () => {
            sound.unload();
            soundRef.current = null;
        };
    }, [url]);

    // Advance the playhead each frame while playing; idle otherwise.
    useEffect(() => {
        if (!isPlaying) return undefined;
        let frame = 0;
        const tick = () => {
            const sound = soundRef.current;
            if (sound) {
                const seek = sound.seek();
                if (typeof seek === 'number') setPosition(seek);
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [isPlaying]);

    if (engine === 'error') {
        return (
            <p role="alert" className="text-xs text-error-500">
                Couldn&apos;t load this recording.
            </p>
        );
    }

    const total = duration > 0 ? duration : (recording.durationSeconds ?? 0);
    const progress = total > 0 ? clamp01(position / total) : 0;
    const isBusy = fetchUrl.isPending || engine === 'loading';

    const handlePlayToggle = (): void => {
        const sound = soundRef.current;
        if (sound) {
            if (isPlaying) sound.pause();
            else sound.play();
            return;
        }
        if (fetchUrl.isPending) return;
        // First play: fetch the presigned url, then the engine effect
        // autoplays — the whole chain stays inside this click gesture.
        fetchUrl.mutate(recording.id, {
            onSuccess: (data) => {
                setEngine('loading');
                setUrl(data.url);
            },
        });
    };

    return (
        <div data-slot="voice-history-recording-player" className="flex items-center gap-2">
            <Button
                type="button"
                variant="secondary"
                size="icon-xs"
                className="shrink-0 rounded-full"
                disabled={isBusy}
                aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
                onClick={handlePlayToggle}
            >
                {isBusy ? (
                    <Loader2 className="size-3 animate-spin" />
                ) : isPlaying ? (
                    <Pause className="size-3" />
                ) : (
                    <Play className="size-3 translate-x-px" />
                )}
            </Button>
            <div className="h-1 min-w-0 flex-1 rounded-full bg-black/[0.08] dark:bg-white/[0.12]">
                <div
                    className="h-full rounded-full bg-primary-500"
                    style={{ width: `${progress * 100}%` }}
                />
            </div>
            <span className="shrink-0 text-[10px] tabular-nums text-text-tertiary">
                {formatCallDuration(Math.floor(position))} / {formatCallDuration(Math.floor(total))}
            </span>
        </div>
    );
}
